import { useState, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { compraAutosservicoHabilitada, funilPublicoHabilitado } from '@/config/funil';
import { Badge } from '@/components/atoms';
import { PersonaSwitcher } from '@/components/PersonaSwitcher/PersonaSwitcher';
import { SeletorIdioma } from '@/components/SeletorIdioma/SeletorIdioma';
import { SeletorMarca } from '@/components/SeletorMarca/SeletorMarca';
import { ThemeSwitcher } from '@/components/ThemeSwitcher/ThemeSwitcher';
import { useFeedDriveHabilitado } from '@/services/feeddriveEstado';
import { usePlatformAccess } from '@/services/platform';
import type { Persona } from '@/components/PersonaSwitcher/types';
import { useAuth } from '@/store/useAuth';
import { useBrand } from '@/store/useBrand';
import { useNavPulse } from '@/store/useNavPulse';
import { usePersona } from '@/store/usePersona';

import styles from './Header.module.css';

type SubItem = { to?: string; label: string; soon?: boolean };
type NavItem = {
  to: string;
  label: string;
  pulse?: boolean;
  submenu?: SubItem[];
  core?: Persona[];
};

export function Header() {
  const { t } = useTranslation(['nav', 'common']);
  const navigate = useNavigate();
  const username = useAuth((s) => s.username);
  const role = useAuth((s) => s.role);
  const encerrarSessao = useAuth((s) => s.encerrarSessao);
  const brand = useBrand((s) => s.brand);
  const producaoPulse = useNavPulse((s) => s.producao);
  const persona = usePersona((s) => s.persona);
  // Instalacao sem FeedDrive (o GNC DESK) nao mostra a porta para ele: a tela
  // so disparava alarmes contra um servico que ali nunca existiu.
  const feedDriveLigado = useFeedDriveHabilitado();

  const mainNav: NavItem[] = useMemo(
    () => [
      {
        to: '/inteligencia',
        label: t('nav:main.intelligence', 'Inteligência'),
        core: ['reporter'],
        submenu: [
          { to: '/inteligencia/blueprint', label: t('nav:sub.blueprint', 'Blueprint Diário (Chefe de Redação)') },
          { to: '/inteligencia', label: t('nav:sub.radar', 'Radar de Inteligência') },
          { to: '/inteligencia/playbook', label: t('nav:sub.playbook', 'Playbook 52 Semanas') },
          { to: '/inteligencia/kanban-estrategico', label: t('nav:sub.kanban', 'Kanban Estratégico') },
          { to: '/inteligencia/calendario', label: t('nav:sub.calendar', 'Calendário Preditivo') },
          ...(feedDriveLigado
            ? [{ to: '/inteligencia/feeddrive', label: t('nav:sub.feeddrive', 'FeedDrive') }]
            : []),
        ],
      },
      { to: '/vigias', label: t('nav:main.watchers', 'Vigias'), core: ['engenheiro', 'reporter'] },
      { to: '/producao', label: t('nav:main.production', 'Produção'), pulse: true, core: ['reporter'] },
      { to: '/jornais', label: t('nav:main.newscasts', 'Jornais'), core: ['reporter'] },
      { to: '/artigos', label: t('nav:main.articles', 'Artigos'), core: ['reporter', 'social'] },
      { to: '/analytics', label: t('nav:main.strategy', 'Estratégia'), core: ['engenheiro'] },
      { to: '/mapa', label: t('nav:main.map', 'Mapa') },
      { to: '/ncnews', label: t('nav:main.management', 'Gestão'), core: ['engenheiro'] },
      {
        to: '/horoscopo',
        label: t('nav:main.horoscope', 'Horóscopo'),
        submenu: [
          { to: '/horoscopo', label: t('nav:main.horoscope', 'Horóscopo') },
          { label: t('nav:sub.market', 'Mercado'), soon: true },
          { label: t('nav:sub.sports', 'Resultados Esportivos'), soon: true },
          { label: t('nav:sub.quotes', 'Cotações'), soon: true },
        ],
      },
      {
        to: '/social',
        label: t('nav:main.social', 'Redes Sociais'),
        core: ['social'],
        submenu: [
          { to: '/social', label: t('nav:sub.publish_social', 'Publicar (X · Bluesky)') },
          { to: '/twitter', label: t('nav:sub.twitter_coverage', 'Cobertura no X') },
          { label: t('nav:sub.instagram', 'Instagram'), soon: true },
          { label: t('nav:sub.facebook', 'Facebook'), soon: true },
          { label: t('nav:sub.youtube', 'YouTube'), soon: true },
          { label: t('nav:sub.tiktok', 'TikTok'), soon: true },
        ],
      },
    ],
    [t, feedDriveLigado],
  );

  const funilLigado = funilPublicoHabilitado();
  const autosservico = compraAutosservicoHabilitada();

  const menuItems: { to: string; label: string; end?: boolean; admin?: boolean; staff?: boolean }[] = useMemo(
    () => [
      { to: '/dashboard', label: t('nav:menu.dashboard', 'Dashboard') },
      // "Planos" é vitrine e vive com o funil. "Faturamento" é AUTOSSERVIÇO e
      // vive com o PSP: enquanto a compra for contact sales e a fatura for
      // manual, essa aba abre um checkout que não cobra ninguém.
      ...(funilLigado ? [{ to: '/planos', label: t('nav:menu.plans', 'Planos') }] : []),
      ...(funilLigado && autosservico
        ? [{ to: '/billing', label: t('nav:menu.billing', 'Faturamento') }]
        : []),
      ...(feedDriveLigado
        ? [{ to: '/inteligencia/feeddrive', label: t('nav:sub.feeddrive', 'FeedDrive') }]
        : []),
      { to: '/engineering', label: t('nav:menu.engineering', 'Engenharia'), admin: true, staff: true },
      { to: '/editorias', label: t('nav:menu.editorials', 'Editorias'), admin: true },
      { to: '/config/portal-esportes', label: t('nav:menu.sports_portal', 'Portal de esportes'), admin: true },
      { to: '/cidades', label: t('nav:menu.cities', 'Cidades') },
      { to: '/stories', label: t('nav:menu.stories', 'Stories') },
      { to: '/config', label: t('nav:menu.settings', 'Configurações'), admin: true },
    ],
    [t, funilLigado, autosservico, feedDriveLigado],
  );

  const nav =
    persona === 'todos'
      ? mainNav
      : [...mainNav].sort((a, b) => {
          const ca = a.core?.includes(persona) ? 0 : 1;
          const cb = b.core?.includes(persona) ? 0 : 1;
          return ca - cb;
        });
  const isCore = (item: NavItem) => persona === 'todos' || (item.core?.includes(persona) ?? false);
  const isAdmin = role === 'admin';
  const { isStaff } = usePlatformAccess();
  const [menuOpen, setMenuOpen] = useState(false);
  const [saindo, setSaindo] = useState(false);

  // L2.1 — "Sair" espera o servidor revogar o token antes de trocar de tela.
  // O `await` não é cerimônia: é ele que garante que a requisição sai com o
  // Authorization ainda em memória. O botão fica travado durante a ida porque
  // um duplo-clique dispararia um segundo logout já sem token — 401, e a tela
  // saltaria para /login por um caminho que não é o do operador.
  // `encerrarSessao` nunca rejeita (trata a falha por dentro e avisa), então a
  // navegação acontece de qualquer jeito: rede caída não pode prender ninguém
  // dentro do sistema.
  async function handleLogout() {
    if (saindo) return;
    setSaindo(true);
    await encerrarSessao();
    navigate('/login', { replace: true });
  }

  return (
    <header className={styles.root}>
      <div className={styles.brand}>
        <button
          type="button"
          className={styles.hamburger}
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
        {brand.logo_url ? (
          <img className={styles.logo} src={brand.logo_url} alt={brand.name} />
        ) : (
          <span className={styles.logoMark} aria-hidden="true">
            <span className={styles.line} />
            <span className={styles.line} />
            <span className={styles.line} />
          </span>
        )}
        <span className={styles.brandName}>{brand.name}</span>
        <Badge tone="neutral" size="xs">
          {t('common:labels.version', 'v1.1')}
        </Badge>

        {menuOpen && (
          <>
            <div className={styles.menuBackdrop} onClick={() => setMenuOpen(false)} />
            <div className={styles.menuPanel} role="menu">
              <div className={styles.menuMain}>
                {mainNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`
                    }
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                ))}
                <div className={styles.menuSep} />
              </div>
              {[
                ...menuItems,
                ...(isStaff
                  ? ([{ to: '/platform', label: t('nav:menu.platform', 'Plataforma'), staff: true }] as typeof menuItems)
                  : []),
              ]
                .filter((i) => (!i.admin || isAdmin) && (!i.staff || isStaff))
                .map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    end={i.end}
                    className={({ isActive }) =>
                      `${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`
                    }
                    onClick={() => setMenuOpen(false)}
                  >
                    {i.label}
                  </NavLink>
                ))}
              <div className={styles.menuSep} />
              <div className={styles.menuPersona}>
                <span className={styles.menuPersonaLabel}>{t('common:labels.view', 'Visão')}</span>
                <PersonaSwitcher />
              </div>
            </div>
          </>
        )}
      </div>

      <nav className={styles.nav} aria-label="Navegação principal">
        {nav.map((item) =>
          item.submenu ? (
            <div key={item.to} className={styles.navItem}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ''} ${
                    isCore(item) ? '' : styles.navDim}`
                }
              >
                {item.label}
                <span className={styles.caret} aria-hidden="true">
                  ▾
                </span>
              </NavLink>
              <div className={styles.submenu} role="menu">
                {item.submenu.map((s) =>
                  s.soon ? (
                    <span key={s.label} className={styles.submenuSoon}>
                      {s.label}
                      <span className={styles.soonTag}>{t('common:status.soon', 'em breve')}</span>
                    </span>
                  ) : (
                    <NavLink
                      key={s.label}
                      to={s.to as string}
                      className={({ isActive }) =>
                        `${styles.submenuItem} ${isActive ? styles.submenuItemActive : ''}`
                      }
                    >
                      {s.label}
                    </NavLink>
                  ),
                )}
              </div>
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''} ${
                  item.pulse && producaoPulse ? styles.pulse : ''
                } ${isCore(item) ? '' : styles.navDim}`
              }
            >
              {item.label}
            </NavLink>
          ),
        )}
      </nav>

      <div className={styles.actions}>
        {/* Configuracoes so existia dentro do menu hamburguer. Num SaaS
            self-service, a tela onde o cliente conecta o site, poe a chave de
            IA e cadastra a equipe nao pode estar escondida atras de tres
            tracinhos — e o operador nao tem por que adivinhar que ela mora la.
            Uma engrenagem no cabecalho, para quem tem permissao de mexer. */}
        {isAdmin && (
          <NavLink
            to="/config"
            className={({ isActive }) =>
              `${styles.gearBtn} ${isActive ? styles.gearBtnActive : ''}`
            }
            title={t('nav:menu.settings', 'Configurações')}
            aria-label={t('nav:menu.settings', 'Configurações')}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
              <path
                d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0c.24.58.8.98 1.42 1H21a2 2 0 1 1 0 4h-.09c-.62.02-1.18.42-1.51 1z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </NavLink>
        )}
        <SeletorMarca />
        <SeletorIdioma />
        <ThemeSwitcher />
        {username && (
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            disabled={saindo}
            aria-busy={saindo}
            title={t('common:labels.logout_title', { username, defaultValue: `Terminar sessão (${username})` })}
          >
            {t('common:actions.logout', 'Sair')}
          </button>
        )}
      </div>
    </header>
  );
}

